import React, { useContext, useState, useEffect } from 'react';
import t from 'react-translate';
import { config } from '@config/pendo.config.json';
import { Button } from 'react-bootstrap';
import { AngularContext } from 'react-app';

import { wrapThunkAction } from 'redux/utils';
import { useAppDispatch } from 'redux/store';
import { disableAccredibleSettings, disableOrgLevelCredlySettings } from 'redux/actions/digital-credential';

import CredlySettings from 'institutions/components/credly-settings';
import SectionContent from '../../section-content';
import DigitalCredentialIntegrationModal from './digital-credential-modal';
import { DisableProps } from '../integrations';
import AccredibleSettings from './accredible-settings';

const DigitalCredentialIntegration = (props: { onDisable: (props: DisableProps) => void }) => {
  const dispatch = useAppDispatch();
  const { injectServices, $scope } = useContext(AngularContext);
  const [InstitutionsManager] = injectServices(['InstitutionsManager']);
  const [showModal, setShowModal] = useState(false);
  const [showCredlyModal, setShowCredlyModal] = useState(false);
  const [showAccredibleModal, setShowAccredibleModal] = useState(false);
  const [isCredlyEnabled, setIsCredlyEnabled] = useState(
    $scope.$eval('vm.InstitutionsManager.institution.isCredlyEnabled'),
  );
  const [isAccredibleEnabled, setIsAccredibleEnabled] = useState(
    $scope.$eval('vm.InstitutionsManager.institution.isAccredibleEnabled'),
  );

  useEffect(() => {
    const unwatchCredly = $scope.$watch(
      'vm.InstitutionsManager.institution.isCredlyEnabled',
      (newValue) => {
        setIsCredlyEnabled(newValue);
      },
    );

    const unwatchAccredible = $scope.$watch(
      'vm.InstitutionsManager.institution.isAccredibleEnabled',
      (newValue) => {
        setIsAccredibleEnabled(newValue);
      },
    );

    return () => {
      unwatchCredly();
      unwatchAccredible();
    };
  }, [$scope]);

  let enabledProvider: 'accredible' | 'credly' | null = null;

  if (isAccredibleEnabled) {
    enabledProvider = 'accredible';
  } else if (isCredlyEnabled) {
    enabledProvider = 'credly';
  }

  const hasEnabled = !!enabledProvider;

  const disableText = enabledProvider === 'accredible'
    ? t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.DISABLE_ACCREDIBLE_INTEGRATION()
    : t.INSTITUTIONS.ADVANCED_SETTINGS.CREDLY.DISABLE();

  const editText = enabledProvider === 'accredible'
    ? t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.EDIT_ACCREDIBLE_INTEGRATION()
    : t.INSTITUTIONS.ADVANCED_SETTINGS.CREDLY.EDIT_CREDLY_INTEGRATION();

  const handleEdit = () => {
    if (enabledProvider === 'credly') {
      setShowCredlyModal(true);
    }

    if (enabledProvider === 'accredible') {
      setShowAccredibleModal(true);
    }
  };

  const handleDisable = () => {
    if (enabledProvider === 'credly') {
      props.onDisable({
        action: () => wrapThunkAction(
          dispatch(
            disableOrgLevelCredlySettings({
              institutionId: InstitutionsManager.institution.id,
            }),
          ),
        ),
        onSuccess: (response) => InstitutionsManager.institution.updateFromReact({
          ...(response?.payload as any).institutionList,
        }),
        title: t.INSTITUTIONS.ADVANCED_SETTINGS.CREDLY.DISABLE(),
        bodyText: t.INSTITUTIONS.ADVANCED_SETTINGS.CREDLY.DISABLE_DESCRIPTION(),
        successMessage: t.INSTITUTIONS.ADVANCED_SETTINGS.CREDLY.DISABLE_SUCCESS(),
        confirmDataQa: config.pendo.credly.disableConfirm,
        cancelDataQa: config.pendo.credly.disableCancel,
      });
    }

    if (enabledProvider === 'accredible') {
      props.onDisable({
        action: () => wrapThunkAction(
          dispatch(
            disableAccredibleSettings({
              institutionId: InstitutionsManager.institution.id,
            }),
          ),
        ),
        onSuccess: (response) => InstitutionsManager.institution.updateFromReact({
          ...(response?.payload as any).institutionList,
        }),
        title: t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.DISABLE_ACCREDIBLE_INTEGRATION(),
        bodyText: t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.DISABLE_DESCRIPTION(),
        successMessage: t.INSTITUTIONS.ADVANCED_SETTINGS.ACCREDIBLE.DISABLE_SUCCESS(),
        confirmDataQa: config.pendo.digitalCredential.accredibleIntegration.disableConfirm,
        cancelDataQa: config.pendo.digitalCredential.accredibleIntegration.disableCancel,
      });
    }
  };

  return (
    <React.Fragment>
      <SectionContent
        header={t.INSTITUTIONS.ADVANCED_SETTINGS.DIGITAL_CREDENTIAL_INTEGRATIONS.TITLE()}
        description={[t.INSTITUTIONS.ADVANCED_SETTINGS.DIGITAL_CREDENTIAL_INTEGRATIONS.DESCRIPTION()]}
        showCTAButton={!hasEnabled}
        ctaText={t.INSTITUTIONS.ADVANCED_SETTINGS.CONFIGURE()}
        onAction={() => setShowModal(true)}
        dataQa={config.pendo.digitalCredential.integrationConfigure}
        extras={hasEnabled && (
          <div className='button-bar mt-6'>
            <Button
              variant='secondary'
              onClick={handleDisable}
              data-qa={
                enabledProvider === 'accredible'
                  ? config.pendo.digitalCredential.accredibleIntegration.integrationDisable
                  : config.pendo.credly.integrationDisable
              }
            >
              {disableText}
            </Button>
            <Button
              variant='primary'
              onClick={handleEdit}
              data-qa={
                enabledProvider === 'accredible'
                  ? config.pendo.digitalCredential.accredibleIntegration.integrationEdit
                  : config.pendo.credly.integrationEdit
              }
            >
              {editText}
            </Button>
          </div>
        )}
      />

      <DigitalCredentialIntegrationModal
        show={showModal}
        onClose={() => setShowModal(false)}
      />

      {/* Credly modal */}
      <CredlySettings
        show={showCredlyModal}
        onClose={() => setShowCredlyModal(false)}
      />

      {/* Accredible modal */}
      <AccredibleSettings
        show={showAccredibleModal}
        onClose={() => setShowAccredibleModal(false)}
      />
    </React.Fragment>
  );
};

export default DigitalCredentialIntegration;
